import React, { Component } from 'react';
import ErrorFallback from './ErrorFallback';

/**
 * Error Boundary component to catch errors in child components
 * and display a fallback UI instead of crashing the whole app
 *
 * Props:
 * - fallback: optional custom fallback UI (replaces the default)
 * - showError: when true, shows error details in the default fallback
 * - resetKeys: array of values; when any value changes, error state is cleared
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
  }

  componentDidUpdate(prevProps) {
    if (!this.state.hasError || !this.props.resetKeys) {
      return;
    }

    const prevKeys = prevProps.resetKeys || [];
    const nextKeys = this.props.resetKeys;
    const hasChanged =
      prevKeys.length !== nextKeys.length ||
      nextKeys.some((key, index) => key !== prevKeys[index]);

    if (hasChanged) {
      this.setState({ hasError: false, error: null });
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorFallback
          error={this.state.error}
          showError={this.props.showError}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
